import { useEffect, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useUser, UserButton } from "@clerk/clerk-react";
import dgptLogo from "@/assets/dgptlogo.png";
import { Button } from "@/components/ui/button";
import { useWorkspaceStore } from "@/store/workspace-store";
import { cn } from "@/lib/utils";

/*
 * Shared shell + primitives for the public Raiseboard pages.
 *
 * These pages run the `raiseboard-theme` skin defined in index.css (cream
 * paper, navy ink, white cards, Playfair display type). Everything here is
 * presentation only — no data fetching, no API surface.
 */

/** Page wrapper: applies the board skin and owns the scroll container. */
export function RaiseBoardShell({ children }: { children: ReactNode }) {
  // The app shell locks body scroll; public board pages scroll their own root.
  useEffect(() => {
    document.body.classList.add("public-route");
    return () => document.body.classList.remove("public-route");
  }, []);

  return (
    <div className="raiseboard-theme h-dvh overflow-y-auto bg-background text-foreground">
      {children}
    </div>
  );
}

/**
 * Top navigation. `onRequestAuth` opens the board sign-in dialog; the caller
 * owns that dialog so the copy can be specific to what the user was doing.
 */
export function RaiseBoardNav({
  onRequestAuth,
  width = "wide",
}: {
  onRequestAuth: (reason: string) => void;
  width?: "wide" | "narrow";
}) {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, user } = useUser();
  const { activeWorkspaceId } = useWorkspaceStore();

  const goHome = () => {
    if (isSignedIn) {
      navigate(activeWorkspaceId ? `/${activeWorkspaceId}/dashboard` : "/workspaces");
    } else {
      navigate("/");
    }
  };

  return (
    <div className="sticky top-0 z-30 border-b border-border/70 bg-card/90 backdrop-blur">
      <div
        className={cn(
          "mx-auto flex h-16 items-center justify-between gap-4 px-4 sm:px-6",
          width === "wide" ? "max-w-6xl" : "max-w-5xl"
        )}
      >
        <div className="flex items-center gap-6">
          <button
            type="button"
            onClick={goHome}
            className="flex items-center gap-2 rounded-md transition-opacity hover:opacity-80"
            aria-label="DiligenceGPT"
          >
            <img src={dgptLogo} alt="DiligenceGPT" className="h-7 w-auto" />
          </button>
          <button
            type="button"
            onClick={() => navigate("/global")}
            className="hidden text-sm font-medium text-muted-foreground transition-colors hover:text-foreground sm:inline-flex"
          >
            Raiseboard
          </button>
          <button
            type="button"
            onClick={() => navigate("/rb")}
            className="hidden text-sm font-medium text-muted-foreground transition-colors hover:text-foreground sm:inline-flex"
          >
            Boards
          </button>
        </div>

        {isLoaded && isSignedIn ? (
          <div className="flex items-center gap-3">
            <span className="hidden truncate text-sm text-muted-foreground sm:inline">
              {user?.firstName
                ? [user.firstName, user.lastName].filter(Boolean).join(" ")
                : user?.primaryEmailAddress?.emailAddress ?? ""}
            </span>
            <UserButton
              appearance={{ elements: { avatarBox: "w-8 h-8" } }}
              afterSignOutUrl={
                typeof window !== "undefined" ? window.location.pathname : "/"
              }
            />
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onRequestAuth("Sign in to continue.")}
              className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground sm:inline-flex"
            >
              Sign In
            </button>
            <Button
              onClick={() => onRequestAuth("Sign in to continue.")}
              className="rounded-full px-5"
            >
              Get started
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** The one card treatment used across both board pages. */
export function RBCard({
  children,
  className,
  padded = true,
  id,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
  /** Anchor target, for pages with a section nav. */
  id?: string;
}) {
  return (
    <div
      id={id}
      className={cn(
        "rounded-2xl border border-border bg-card shadow-sm",
        padded && "p-5 sm:p-6",
        className
      )}
    >
      {children}
    </div>
  );
}

/** Small-caps section heading — the board's structural rhythm. */
export function SectionLabel({
  children,
  as: Tag = "h2",
  className,
}: {
  children: ReactNode;
  as?: "h2" | "h3" | "p";
  className?: string;
}) {
  return (
    <Tag
      className={cn(
        "text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground",
        className
      )}
    >
      {children}
    </Tag>
  );
}

/** Neutral topic / metadata pill. */
export function RBTag({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-medium text-foreground/70">
      {children}
    </span>
  );
}

/** "Actively Raising" state chip — the only place green appears. */
export function StatusChip({
  children = "Actively Raising",
  size = "sm",
}: {
  children?: ReactNode;
  size?: "sm" | "md";
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 font-medium text-emerald-700",
        size === "md" ? "px-3 py-1.5 text-sm" : "px-2.5 py-1 text-xs"
      )}
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
      {children}
    </span>
  );
}

/** Dot-separated run of muted metadata. Skips empty values. */
export function MetaRow({
  items,
  className,
}: {
  items: Array<ReactNode | null | undefined | false>;
  className?: string;
}) {
  const parts = items.filter(Boolean) as ReactNode[];
  if (parts.length === 0) return null;
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground",
        className
      )}
    >
      {parts.map((part, i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          {i > 0 && <span className="select-none text-border">·</span>}
          {part}
        </span>
      ))}
    </div>
  );
}
